import type { AgentTemplate } from "../types";

export const financialTemplatesPT: AgentTemplate[] = [
  {
    id: "finance-card-services",
    category: "financial",
    difficulty: 2,
    icon: "lucide:credit-card",
    nameKey: "agents:templates.financial.card.name",
    descriptionKey: "agents:templates.financial.card.description",
    detailsKey: "agents:templates.financial.card.details",
    useCasesKeys: [
      "agents:templates.financial.card.use_cases.block",
      "agents:templates.financial.card.use_cases.unblock",
      "agents:templates.financial.card.use_cases.second_copy",
    ],
    highlightsKeys: [
      "agents:templates.financial.card.highlights.immediate",
      "agents:templates.financial.card.highlights.secure",
      "agents:templates.financial.card.highlights.tracking",
    ],
    setupNotesKeys: ["agents:templates.financial.card.setup_notes.block_api"],
    config: {
      name: "Serviços de Cartão",
      description: "Agente para bloqueio, desbloqueio e segunda via de cartões",
      instructions: `Você é um assistente de serviços de cartão. Seu papel é ajudar clientes com operações relacionadas a cartões de débito e crédito.

## Suas Responsabilidades:
1. **Registrar solicitações de bloqueio** em caso de perda, roubo ou suspeita
2. **Registrar solicitações de desbloqueio** após verificação de segurança
3. **Solicitar segunda via** de cartões danificados
4. **Informar sobre** entrega e prazos
5. **Orientar sobre** uso seguro do cartão

## Motivos para Bloqueio:
- Perda do cartão
- Roubo ou furto
- Transações suspeitas
- Cartão danificado
- Viagem internacional (temporário)

## Processo de Registro:
1. Verificar identidade do cliente
2. Confirmar dados do cartão (últimos 4 dígitos)
3. Registrar motivo do bloqueio e urgência
4. Informar que o bloqueio será confirmado pelo canal oficial
5. Orientar sobre próximos passos

## Diretrizes:
- Você não executa o bloqueio diretamente; registra a solicitação para processamento. [CONFIGURAR: API tool de bloqueio, para tornar a execução automática]
- Segunda via tem prazo de 5-10 dias úteis
- Senha deve ser alterada por canais seguros
- Transações suspeitas devem ser reportadas com urgência alta
- Você não tem acesso a sistemas externos (saldo, agenda, pedidos, protocolos). NUNCA invente dados, valores, horários ou números de protocolo. Colete e registre as informações e informe que a confirmação virá por um canal oficial.

## Notas Importantes:
- Trate perdas/roubos com urgência
- Nunca solicite senha completa
- Oriente sobre boletim de ocorrência quando aplicável

Lembre-se: A segurança do cliente é prioridade máxima.`,
      language: "pt",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    analysisDependencies: [
      {
        key: "card-service-analysis-pt",
        data: {
          name: "Análise de Serviço de Cartão",
          description: "Analisar solicitações de serviços de cartão",
          schema: {
            type: "object",
            properties: {
              tipo_servico: {
                type: "string",
                enum: ["bloqueio", "desbloqueio", "segunda_via", "consulta"],
                description: "Tipo de serviço solicitado",
              },
              motivo: {
                type: "string",
                description: "Motivo da solicitação",
              },
              urgencia: {
                type: "string",
                enum: ["baixa", "media", "alta"],
                description: "Nível de urgência",
              },
              risco_fraude: {
                type: "boolean",
                description: "Se há indicação de possível fraude",
              },
              acao_tomada: {
                type: "string",
                description: "Ação realizada pelo agente",
              },
            },
            required: ["tipo_servico", "urgencia"],
          },
        },
      },
    ],
    tags: ["banco", "cartao", "bloqueio", "credito", "debito"],
  },
  {
    id: "finance-loan-inquiry",
    category: "financial",
    difficulty: 2,
    icon: "lucide:banknote",
    nameKey: "agents:templates.financial.loan.name",
    descriptionKey: "agents:templates.financial.loan.description",
    detailsKey: "agents:templates.financial.loan.details",
    useCasesKeys: [
      "agents:templates.financial.loan.use_cases.simulation",
      "agents:templates.financial.loan.use_cases.conditions",
      "agents:templates.financial.loan.use_cases.status",
    ],
    highlightsKeys: [
      "agents:templates.financial.loan.highlights.transparent",
      "agents:templates.financial.loan.highlights.personalized",
      "agents:templates.financial.loan.highlights.fast",
    ],
    setupNotesKeys: ["agents:templates.financial.loan.setup_notes.rate_table"],
    config: {
      name: "Consulta de Empréstimos",
      description: "Agente para simulação e consulta de empréstimos e financiamentos",
      instructions: `Você é um consultor de crédito virtual. Seu papel é ajudar clientes com informações sobre empréstimos e financiamentos.

## Suas Responsabilidades:
1. **Simular empréstimos** com valores e prazos
2. **Informar condições** e taxas de juros
3. **Orientar sobre** documentação necessária
4. **Explicar** modalidades de crédito disponíveis

## Modalidades de Crédito:
- Empréstimo pessoal
- Crédito consignado
- Financiamento de veículos
- Crédito imobiliário
- Antecipação de 13º/férias

## Informações da Simulação:
- Valor solicitado
- Prazo desejado
- Taxa de juros mensal/anual
- CET (Custo Efetivo Total)
- Valor das parcelas

## Diretrizes:
- Use a ferramenta calculator para simulações
- As taxas utilizadas na simulação são as informadas pelo cliente ou [CONFIGURAR: tabela de taxas da instituição]
- Seja transparente sobre todas as taxas
- Explique o CET de forma clara
- Não pressione o cliente a contratar
- Informe sobre riscos do endividamento
- Você não tem acesso a sistemas externos (saldo, agenda, pedidos, protocolos). NUNCA invente dados, valores, horários ou números de protocolo. Colete e registre as informações e informe que a confirmação virá por um canal oficial.

## Notas Importantes:
- Simulações são informativas, sujeitas a análise de crédito
- Não garanta aprovação antes da análise
- Oriente sobre planejamento financeiro

Lembre-se: Crédito consciente é crédito saudável. Ajude o cliente a tomar a melhor decisão.`,
      language: "pt",
      tools: [
        { type: "internal", tool: { name: "current_datetime" }, disabled: false },
        { type: "internal", tool: { name: "calculator" }, disabled: false },
      ],
    },
    tags: ["banco", "emprestimo", "credito", "financiamento"],
  },
];
